import Core from './src/core/Core';
import ProBalancer from './src/balancer/CdnBalancer';
import npaw from './src/analytics/npawlib';
import DiagnosticTool from './src/diagnostic/DiagnosticTool';
import AppAnalytics from './src/appAnalytics/AppAnalytics';
import Log, { LogLevel } from './src/common/log';
global.LogLevel = LogLevel;
export default class UnifiedPlugin {
    constructor(accountCode) {
        this.core = Core.getInstance(accountCode);
        this.proBalancer = new ProBalancer(accountCode);
        this.videoAnalytics = npaw;
        this.appAnalytics = new AppAnalytics(accountCode);
        this.plugin = new npaw.Plugin(this.appAnalytics);
        this.diagnostic = DiagnosticTool.getInstance(true);
        this.diagnostic.start();
    }
    destroy() {
        this.core.destroy();
        this.proBalancer.destroy();
        this.plugin.videos.getVideoKeys().forEach((videoKey) => {
            var _a, _b;
            (_a = this.plugin.getAdapter(videoKey)) === null || _a === void 0 ? void 0 : _a.fireStop();
            (_b = this.plugin.getAdsAdapter(videoKey)) === null || _b === void 0 ? void 0 : _b.fireStop();
        });
        this.plugin.destroy();
        this.diagnostic.destroy();
    }
    /**
     * Changes the log level of the Unified Plugin. See {@link LogLevel} for the different levels available
     *
     * @param logLevel Log level to be used
     */
    setLogLevel(logLevel) {
        Log.setLogLevel(logLevel);
    }
    /**
     * Sets Balancer options. See {@link BalancerOptions}.
     * @param options Balancer options
     */
    setBalancerOptions(options) {
        this.proBalancer.setOptions(options);
    }
    /**
     * Sets the manifest URL of the played content in the Balancer options object.
     * For url changes the loader is restarted.
     * @param url Manifest url of the content being played.
     * @public
     */
    setManifestResource(url) {
        this.proBalancer.setManifestResource(url);
    }
    /**
     * Adds an interceptor to the Balancer
     * @param name Name of the interceptor
     * @param player Video player object
     */
    addInterceptor(name, player) {
        this.proBalancer.addInterceptor(name, player);
    }
    /**
     * Returns Bitmovin configuration for request interception
     */
    getBitmovinSendHttpRequest() {
        return this.proBalancer.getBitmovinSendHttpRequest();
    }
    /**
     * Returns the manifest/video URL to be used following the Selector API.
     * If an error occurs this will return undefined
     */
    getRecommendedManifest() {
        return this.proBalancer.getRecommendedManifest();
    }
    /**
     * Register a video player within the Video Analytics module
     * If there already is a registered ads adapter, it is removed first.
     *
     * @param player Video player instance
     * @param adapterJsonCode JSON code with the adapter configuration
     * @param options Analytics options
     * @param videoKey Custom video identifier
     * @param callback Callback to be called after the adapter is registered
     */
    registerAdapter(player, adapterUrl, options, videoKey, callback) {
        this.registerAdapterInModule(false, player, adapterUrl, options, videoKey, callback);
    }
    /**
     * Registers a barebones player adapter without any intrinsic behavior within the Video Analytics module.
     * If there already is a registered ads adapter, it is removed first.
     *
     * @param videoKey Custom video identifier
     * @param options Analytics options
     */
    registerDefaultAdapter(videoKey, options) {
        this.plugin.registerAdapter(undefined, '{}', options, videoKey);
    }
    /**
     * Removes a registered video adapter. Optionally, a video identifier can be passed.
     *
     * @param videoKey Custom video identifier
     */
    removeAdapter(videoKey) {
        if (!videoKey || this.plugin.videos.existsVideo(videoKey)) {
            this.getAdapter(videoKey).fireStop();
        }
        this.plugin.removeAdapter(videoKey);
    }
    /**
     * Returns the registered player adapter. Optionally, a video identifier can be passed.
     *
     * @param videoKey Custom video identifier
     */
    getAdapter(videoKey) {
        return this.plugin.getAdapter(videoKey);
    }
    /**
     * Returns the registered ads adapter. Optionally, a video identifier can be passed.
     *
     * @param videoKey Custom video identifier
     */
    getAdsAdapter(videoKey) {
        return this.plugin.getAdsAdapter(videoKey);
    }
    /**
     * Register an ads player within the Ads Analytics module.
     * If there already is a registered ads adapter, it is removed first.
     *
     * @param player Ads player/manager
     * @param adapterJsonCode JSON code with the adapter configuration
     * @param options Analytics options
     * @param videoKey Custom video identifier
     */
    registerAdsAdapter(player, adapterUrl, options, videoKey, callback) {
        this.registerAdapterInModule(true, player, adapterUrl, options, videoKey, callback);
    }
    /**
     * Registers a barebones ads adapter without any intrinsic behavior within the Video Analytics module.
     * If there already is a registered ads adapter, it is removed first.
     *
     * @param videoKey Custom video identifier
     * @param options Analytics options
     */
    registerDefaultAdsAdapter(videoKey, options) {
        this.plugin.registerAdsAdapter(undefined, '{}', options, videoKey);
    }
    /**
     * Removes a registered ads adapter. Optionally, a video identifier can be passed.
     *
     * @param videoKey Custom video identifier
     */
    removeAdsAdapter(videoKey) {
        this.plugin.removeAdsAdapter(videoKey);
    }
    /**
     * Sets Analytics options. See {@link npaw.Options.setOptions}.
     *
     * @param options Analytics options object
     * @param videoKey Custom video identifier
     */
    setAnalyticsOptions(options, videoKey) {
        this.plugin.setOptions(options, videoKey);
        this.appAnalytics.setOptions(this.plugin.options);
        this.core.setOptions(this.plugin.options);
        this.diagnostic.setOptions(this.plugin.options);
    }
    registerAdapterInModule(isAds, player, adapterUrl, options, videoKey, callback) {
        if (adapterUrl.length === 0) {
            Log.error("Adapter URL can't be empty!");
        }
        // If fetch doesn't exist use XHR instead
        if (typeof fetch != 'undefined') {
            fetch(adapterUrl)
                .then((res) => res.json())
                .then((adapterJson) => {
                const adapterJsonCode = JSON.stringify(adapterJson);
                if (!isAds) {
                    this.plugin.registerAdapter(player, adapterJsonCode, options, videoKey);
                }
                else {
                    this.plugin.registerAdsAdapter(player, adapterJsonCode, options, videoKey);
                }
                callback === null || callback === void 0 ? void 0 : callback();
            });
        }
        else {
            const xhr = new XMLHttpRequest();
            xhr.addEventListener('readystatechange', () => {
                if (xhr.readyState === 4) {
                    if (xhr.status >= 200 && xhr.status < 400) {
                        const adapterJsonCode = xhr.response;
                        if (!isAds) {
                            this.plugin.registerAdapter(player, adapterJsonCode, options, videoKey);
                        }
                        else {
                            this.plugin.registerAdsAdapter(player, adapterJsonCode, options, videoKey);
                        }
                        callback === null || callback === void 0 ? void 0 : callback();
                    }
                }
            });
            xhr.open('GET', adapterUrl);
            xhr.send();
        }
    }
}
